import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { SignIn } from '../pages/auth/screens/signin';
import VendorPaymentReport from '../pages/reports/screens/VendorPaymentReport';
import ChargerStatusAnalytics from '../pages/reports/screens/ChargerStatusAnalytics';
import VendorChargerStatus from '../pages/vendorReports/screens/VendorChargerStatus';
import VendorChargingReport from '../pages/vendorReports/screens/VendorChargingReport';
import VendorCommissionPage from '../pages/commission/screens/VendorCommission';
import Layout from '../elements/Layout';
import { isAuthenticated, isAdmin, getToken } from '../utils/auth';

// Intercept 401 responses globally and redirect to login
const setup401Interceptor = (navigate: ReturnType<typeof useNavigate>) => {
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    if (response.status === 401 && isAuthenticated()) {
      sessionStorage.removeItem('authorization');
      navigate('/');
    }
    return response;
  };
};

const AuthInterceptor: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  useEffect(() => {
    setup401Interceptor(navigate);
  }, [navigate]);
  return <>{children}</>;
};

// Protected route — redirects to login if not authenticated
const ProtectedRoute: React.FC<{ children: React.ReactNode; adminOnly?: boolean }> = ({
  children,
  adminOnly = false,
}) => {
  if (!isAuthenticated()) return <Navigate to="/" replace />;
  if (adminOnly && !isAdmin()) {
    return <Navigate to="/vendor/charger-status" replace />;
  }
  return <Layout>{children}</Layout>;
};

const Navigator: React.FC = () => {
  return (
    <Router>
      <AuthInterceptor>
        <Routes>
          {/* Public */}
          <Route path="/" element={<SignIn />} />

          {/* Admin routes */}
          <Route
            path="/admin/vendor-payments"
            element={
              <ProtectedRoute adminOnly>
                <VendorPaymentReport />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/charger-status"
            element={
              <ProtectedRoute adminOnly>
                <ChargerStatusAnalytics />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/vendor-commission"
            element={
              <ProtectedRoute adminOnly>
                <VendorCommissionPage />
              </ProtectedRoute>
            }
          />

          {/* Vendor routes (admin can also access) */}
          <Route
            path="/vendor/charger-status"
            element={
              <ProtectedRoute>
                <VendorChargerStatus />
              </ProtectedRoute>
            }
          />
          <Route
            path="/vendor/charging-report"
            element={
              <ProtectedRoute>
                <VendorChargingReport />
              </ProtectedRoute>
            }
          />

          {/* Default redirect */}
          <Route
            path="/dashboard"
            element={
              <Navigate
                to={isAuthenticated() && isAdmin() ? '/admin/vendor-payments' : '/vendor/charger-status'}
                replace
              />
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthInterceptor>
    </Router>
  );
};

export default Navigator;
